import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from '@/utilities/useQuery';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import cache from '@/graphql/cache';
import * as Sentry from '@sentry/react';

import { parsePdf } from './queries/parsePdf';
import { createOrderJob } from './queries/createOrderJob';
import { QUERY_DIMENSIONS_BY_SKUS, QUERY_JOBS_BY_CLIENT } from './graphql/queries';
import { SHIPMENT_FORM_TYPES, SHIPMENT_FORM_MODAL_PROGRESSION, SHIPMENT_FORM_STATE } from './constants';
import { useShipmentFormCallbacks, useOrderErrors } from './hooks/form';
import { ServiceCatalogProvider } from './hooks/serviceCatalog';
import { generateOptions } from './utilities/generateOptions';
import { buildForwardStockingPickupInfo } from './utilities/forwardStockingPrefill';
import { shouldApplyMiddleMileDefault } from './utilities/orderTypeOptions';
import { useClientUser } from '@/hooks';

const POLL_INVERVAL = 30 * 1000; //30s

export const Context = createContext();

const ShipmentFormContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const forwardStocking = location.state?.forwardStocking || null;
    const prefillRef = useRef(forwardStocking);
    const { settings, circles } = useClientUser();
    const [state, setState] = useState(SHIPMENT_FORM_STATE.CREATE);
    const [formType, setFormType] = useState(null);
    const [progression, setProgression] = useState({
        current: 0,
        type: null,
    });

    const [orders, setOrders] = useState({});
    const [multiPickup, setMultiPickup] = useState(false);
    const [pickupInfo, setPickupInfo] = useState({});
    const [uploadedFile, setUploadedFile] = useState(null);
    const [freightType, setFreightType] = useState('household');
    const [mapping, setMapping] = useState({});
    const [editing, setEditing] = useState(null);
    const [editingPickup, setEditingPickup] = useState(null);
    const [deleteOrderModal, setDeleteOrderModal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [templateName, setTemplateName] = useState(null);
    const [templateKey, setTemplateKey] = useState(null);
    const [pdf, setPdf] = useState({});

    const [successMsg, setSuccessMsg] = useState('');
    const [warningMsg, setWarningMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (settings?.import?.defaultFreightType) {
            setFreightType(settings.import.defaultFreightType);
        }

        if (circles?.['import-default-mm-fm']) {
            setPickupInfo((prev) => {
                if (!shouldApplyMiddleMileDefault(prev)) {
                    return prev;
                }
                return {
                    ...prev,
                    is_middle_mile: true,
                    is_middle_mile_origin: !circles?.['import-default-receiving'],
                };
            });
        }
    }, [settings, circles]);

    const csvOptions = useMemo(() => {
        return Object.fromEntries(generateOptions(multiPickup).map((option) => [option.key, option]));
    }, [multiPickup]);

    const [fetchJobs, { data: jobs, refetch }] = useLazyQuery(QUERY_JOBS_BY_CLIENT, {
        pollInterval: POLL_INVERVAL,
    });

    const [submitOrders, { loading: submitting }] = useQuery(createOrderJob, {
        onComplete: (resp) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'jobs' });
            setSuccessMsg('Order(s) successfully submitted!');

            if (resp?.data?.jobs.length > 0) {
                navigate(`/job/${resp.data.jobs[0]}`);
            } else {
                refetch();
            }
        },
        onError: (err) => {
            console.error(err);
            setErrorMsg('Failed to save orders!');
            refetch();

            Sentry.captureException(err);
        },
    });

    const [processPdf, { loading: processingPdf }] = useQuery(parsePdf, {
        onError: (err) => {
            console.error(err);
            setErrorMsg('Failed to parse pdf!');
            refetch();

            Sentry.captureException(err);
        },
    });

    // Network-only for the same reason as the item row lookup: a stale row would be copied onto every
    // order in the file, then written back to the SKU when they save.
    const apolloClient = useApolloClient();
    const lookupSkus = useCallback(
        (skus, shipperId) =>
            apolloClient
                .query({
                    query: QUERY_DIMENSIONS_BY_SKUS,
                    variables: { skus, client_id: shipperId },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => data?.results || []),
        [apolloClient]
    );

    // PDF and JSON imports have no single shipment-level pickup (pickup is routed
    // per-order), and multi-pickup is per-order too — so group them: pickup becomes
    // a per-order concern (PICKUP tab) and the PickupInfoModal pickup section is
    // suppressed. Drives both the per-order error projection below (reachable tabs)
    // and the Context value.
    const groupedPickup = multiPickup || formType === SHIPMENT_FORM_TYPES.PDF || formType === SHIPMENT_FORM_TYPES.JSON;

    const missingFieldsByOrder = useOrderErrors({ orders, pickupInfo, groupedPickup });

    const callbacks = useShipmentFormCallbacks(
        {
            orders,
            formType,
            freightType,
            mapping,
            pickupInfo,
            progression,
            state,
            uploadedFile,
            templateName,
            csvOptions,
            multiPickup,
            pdf,
        },
        {
            setEditing,
            setEditingPickup,
            setErrorMsg,
            setFormType,
            setFreightType,
            setLoading,
            setMapping,
            setOrders,
            setMultiPickup,
            setPickupInfo,
            setProgression,
            setTemplateName,
            setState,
            setSuccessMsg,
            setUploadedFile,
            setWarningMsg,
            submitOrders,
            processPdf,
            lookupSkus,
            navigate,
            pendingForwardStockingRef: prefillRef,
        }
    );

    useEffect(() => {
        const prefill = prefillRef.current;
        if (!prefill) return;

        setPickupInfo((prev) => ({
            ...prev,
            ...buildForwardStockingPickupInfo(prefill),
        }));
        callbacks.selectFormType(SHIPMENT_FORM_TYPES.MANUAL);
        navigate(location.pathname, { replace: true, state: {} });
    }, []);

    return (
        <Context.Provider
            value={{
                state: {
                    jobs,
                    isFirstStep: progression.current === 0,
                    current: (SHIPMENT_FORM_MODAL_PROGRESSION[progression.type] || [])[progression.current],
                    groupedPickup,
                    editing,
                    editingPickup,
                    deleteOrderModal,
                    formState: state,
                    formType,
                    freightType,
                    isLoading: loading,
                    isSubmitting: submitting,
                    isProcessingPdf: processingPdf,
                    orders,
                    pickupInfo,
                    savedMapping: mapping,
                    templateName,
                    templateKey,
                    uploadedFile,
                    csvOptions,
                    multiPickup,
                    pdf,
                },
                callbacks: {
                    ...callbacks,
                    setOrders,
                    setErrorMsg,
                    setEditing,
                    setEditingPickup,
                    setDeleteOrderModal,
                    setFormType,
                    setFreightType,
                    setMapping,
                    setProgression,
                    setSuccessMsg,
                    setUploadedFile,
                    setWarningMsg,
                    setLoading,
                    setPdf,
                    setTemplateName,
                    setTemplateKey,
                    fetchJobs,
                    setMultiPickup,
                },
                errors: {
                    errorMsg,
                    missingFieldsByOrder,
                    successMsg,
                    warningMsg,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const ContextProvider = ({ children }) => (
    <ServiceCatalogProvider>
        <ShipmentFormContextProvider>{children}</ShipmentFormContextProvider>
    </ServiceCatalogProvider>
);
